import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";

// LocalStorage key for the last successfully-activated org id, keyed
// per-user so a shared device doesn't leak the previous user's
// preference into the next login. Written by ``SidebarOrgHeader``'s
// manual switch AND by this hook after a successful auto-switch;
// read here on boot so a returning user lands on the org they were
// last using instead of the server's default fallback (lowest-pk for
// platform staff, which is rarely what they want when they own
// multiple orgs). Value is a stringified integer id.
export const lastActiveOrgKey = (userId: number | string) => `afri:last-active-org:${userId}`;

/**
 * After the ``me`` query resolves, make sure the user has an active
 * organization. This is the frontend safety net for the boot path:
 *
 *   - Backend: ``LSTokenObtainPairSerializer`` already assigns
 *     ``active_organization`` on JWT login, and ``UserWhoAmIAPI``'s
 *     ``_repair_active_organization`` re-runs on every whoami.
 *   - Frontend: this hook covers the gap when both server-side paths
 *     left ``active_organization = null`` (race condition, stale
 *     mirror, user pre-dates the backend fix). We fetch the user's
 *     organizations and ``POST /organizations/switch`` into the
 *     first one, then invalidate ``current-user`` so the rest of the
 *     UI (sidebar, breadcrumbs, project list) sees the new active
 *     org without a page reload.
 *
 * Returns ``{ orgs, fetchOk }`` so callers can branch on the
 * authoritative org-list state — used by ``RootPage`` to decide
 * whether to render the standard shell or the "you're not in any
 * organization yet" empty state. ``fetchOk`` is true ONLY after a
 * successful round trip; if the cloud is unreachable we leave it
 * false so the UI doesn't flash the no-org empty state in place
 * of the offline cache.
 */
export const useEnsureActiveOrg = (): {
  orgs: Array<{ id: number; title: string }>;
  fetchOk: boolean;
} => {
  const api = useAPI();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [orgs, setOrgs] = useState<Array<{ id: number; title: string }>>([]);
  const [fetchOk, setFetchOk] = useState(false);
  // Guard against double-firing the switch when React StrictMode
  // mounts the hook twice in dev.
  const switching = useRef(false);
  // One-shot guard: we attempt the auto-switch at most once per mount.
  // The authoritative org assignment is server-side (whoami's
  // _repair_active_organization always lands the user — staff included —
  // on a usable org), so this hook is only a fast-path. A ref that never
  // resets means that even if the server kept returning a null active org
  // we could not spin into a switch→refetch→switch reload loop.
  const attempted = useRef(false);

  // Retry counter so a failed auto-switch (e.g. server 502, race with
  // repair) can try again on the next whoami tick instead of giving
  // up permanently. Capped at 3 to prevent infinite loops.
  const attemptCount = useRef(0);
  const MAX_ATTEMPTS = 3;

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const res = await api.callApi("organizations", { suppressError: true });
      if (cancelled) return;
      // Diagnostic — logs on every effect fire so a user reporting
      // "I have to manually pick my org after login" can share the
      // console output and we see exactly what came back from the
      // orgs list, what the user's active_organization was, and
      // whether the switch call succeeded / errored.
      console.info("[useEnsureActiveOrg] tick", {
        user_id: user.id,
        active_org: user.active_organization,
        is_staff: (user as { is_staff?: boolean }).is_staff,
        attempt: attemptCount.current,
        resType: Array.isArray(res) ? "array" : typeof res,
        resLen: Array.isArray(res) ? res.length : "n/a",
      });
      // Distinguish "fetched and empty" from "fetch failed":
      //   - transport error (offline) → ``res`` is null → keep
      //     fetchOk=false so the no-org empty state doesn't flash
      //     up just because the cloud is unreachable.
      //   - 403 / 401 from the server → user has no org membership
      //     and the endpoint refused. Treat that as "fetched and
      //     empty" — the user is genuinely orgless, NoOrgState
      //     should render. Without this branch, an orphan user
      //     would see the project routes 403 across the board.
      //   - 200 with [] → fetched and empty, NoOrgState renders.
      //   - 200 with list → normal orgs.
      const errStatus = (res as { status?: number } | null)?.status;
      const isAuthError = errStatus === 401 || errStatus === 403;
      const ok = res != null && (!(res as { error?: unknown }).error || isAuthError);
      if (!ok) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const list: Array<{ id: number; title: string }> = isAuthError
        ? []
        : Array.isArray(res)
          ? res
          : ((res as any)?.results ?? []);
      setOrgs(list);
      setFetchOk(true);
      // Save-on-observation: every time we see the user with a
      // resolved active_organization that appears in their org list,
      // stamp it as the "last active" for next boot. This is the
      // primary persistence path — save-on-manual-switch (in
      // SidebarOrgHeader / OrgSwitcher / PlatformOrgsPage) is a
      // faster nudge, but this observation path guarantees that even
      // an implicit switch (whoami repair, server-side default) is
      // remembered without the user having to touch the dropdown.
      try {
        const activeIdNow = user.active_organization ?? null;
        if (activeIdNow != null && list.some((o) => o.id === activeIdNow) && typeof window !== "undefined") {
          window.localStorage.setItem(lastActiveOrgKey(user.id), String(activeIdNow));
        }
      } catch {
        /* private browsing */
      }
      // Prefer the user's remembered last-active org — the one they
      // manually picked (or the auto-switch previously landed on)
      // in their last session. Only honour it if it still appears
      // in the current org list (the membership could have been
      // revoked, or the org deleted, since we saved it). Falls
      // through to the "current active org" check + first-org
      // fallback below.
      let remembered: number | null = null;
      try {
        if (typeof window !== "undefined") {
          const raw = window.localStorage.getItem(lastActiveOrgKey(user.id));
          const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
          if (!Number.isNaN(parsed) && list.some((o) => o.id === parsed)) {
            remembered = parsed;
          }
        }
      } catch {
        /* private browsing */
      }
      // Short-circuit when we're already on the right org: no
      // remembered pref AND active_organization is already set, OR
      // active_organization already matches the remembered pref.
      const activeId = user.active_organization ?? null;
      // The server's active org is the truth whenever it is a valid choice
      // for this user: any org they belong to, or ANY org for platform
      // staff (Platform → Organizations → View switches staff into orgs
      // they are not members of on purpose). Auto-switching only exists to
      // rescue a user with NO usable active org — it must never pull
      // staff back out of an org they deliberately entered (that surfaced
      // as "project no longer exists" + a surprise org change mid-review).
      const isStaff =
        !!(user as { is_staff?: boolean; is_superuser?: boolean }).is_staff ||
        !!(user as { is_superuser?: boolean }).is_superuser;
      const activeIsValid = activeId != null && (isStaff || list.some((o) => o.id === activeId));
      if (activeIsValid) return;
      const alreadyOnRemembered = remembered != null && activeId === remembered;
      const noRememberedButHaveActive = remembered == null && activeId != null;
      if (alreadyOnRemembered || noRememberedButHaveActive) return;
      if (switching.current || attempted.current) return;
      if (attemptCount.current >= MAX_ATTEMPTS) {
        console.warn(
          "[useEnsureActiveOrg] auto-switch gave up after",
          MAX_ATTEMPTS,
          "attempts — user needs to pick manually",
        );
        return;
      }
      // Switch target: remembered wins over first-in-list. Staff with no
      // membership are handled server-side (whoami's repair assigns them a
      // usable org), so we intentionally do NOT reach into the platform-wide
      // org list here — doing that previously fought the backend repair and
      // span the boot into an infinite reload loop.
      const target = remembered != null ? (list.find((o) => o.id === remembered) ?? list[0]) : list[0];
      if (!target?.id) return;
      switching.current = true;
      attemptCount.current += 1;
      console.info("[useEnsureActiveOrg] attempting switch to org", target.id, `(${target.title})`);
      const switchRes = await api.callApi("switchOrganization", {
        body: { organization_id: target.id },
        suppressError: true,
      });
      if (cancelled) return;
      switching.current = false;
      // Only lock ``attempted`` on a SUCCESSFUL switch. A failed switch
      // (network blip, cloud 502, race with server-side repair) used
      // to flip the guard permanently, trapping the user on the
      // no-active-org state until a manual pick. Now the next effect
      // fire — triggered by any subsequent whoami refetch that still
      // returns ``active_organization: null`` — gets another attempt.
      if (!switchRes || switchRes.error) {
        console.error("[useEnsureActiveOrg] switch failed:", switchRes);
        // Force a refetch so the useEffect deps change and we can
        // retry. Otherwise the effect only re-fires on user.id or
        // user.active_organization change; a failed switch changes
        // neither, so without an explicit refetch this hook would
        // silently stop trying.
        if (attemptCount.current < MAX_ATTEMPTS) {
          console.info("[useEnsureActiveOrg] scheduling retry via query invalidation");
          window.setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ["current-user"] });
          }, 1500 * attemptCount.current); // 1.5s, 3s, 4.5s
        }
        return;
      }
      console.info("[useEnsureActiveOrg] switch succeeded to org", target.id);
      attempted.current = true;
      // Persist for next boot so a returning user lands here
      // without another switch round-trip.
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(lastActiveOrgKey(user.id), String(target.id));
        }
      } catch {
        /* private browsing */
      }
      // Optimistic cache update — set ``active_organization`` on the
      // current ``me`` payload immediately so the sidebar / breadcrumb
      // / project list re-render now, instead of waiting on the
      // refetch round trip. The subsequent ``invalidateQueries`` still
      // reconciles against the server, but the UI is already in the
      // post-switch state. Without this, the user perceives a 1-2s
      // window where "No organization" sits in the sidebar after
      // login — long enough that they reach for it manually.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(["current-user"], (prev: any) =>
        prev ? { ...prev, active_organization: target.id } : prev,
      );
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.active_organization]);

  return { orgs, fetchOk };
};
